/**
 * 渲染性能优化工具函数
 */

// 检测设备性能等级
export const getDevicePerformanceLevel = () => {
  const hardwareConcurrency = navigator.hardwareConcurrency || 4
  const memory = navigator.deviceMemory || 4
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  const isLowEnd = hardwareConcurrency <= 4 || memory <= 4 || isMobile

  if (isLowEnd) {
    return 'low'
  } else if (hardwareConcurrency <= 8 || memory <= 8) {
    return 'medium'
  } else {
    return 'high'
  }
}

// 检测是否支持硬件加速
export const supportsHardwareAcceleration = () => {
  const canvas = document.createElement('canvas')
  const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
  return !!gl
}

// 检测是否支持backdrop-filter
export const supportsBackdropFilter = () => {
  return CSS.supports('backdrop-filter', 'blur(1px)')
}

// 检测用户是否偏好减少动画
export const prefersReducedMotion = () => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

// 应用渲染性能优化策略
export const applyRenderOptimizations = () => {
  const performanceLevel = getDevicePerformanceLevel()
  const supportsHardware = supportsHardwareAcceleration()
  const supportsBackdrop = supportsBackdropFilter()
  const reducedMotion = prefersReducedMotion()

  // 添加性能等级类到body
  document.body.classList.add(`performance-${performanceLevel}`)

  // 如果不支持硬件加速，添加降级类
  if (!supportsHardware) {
    document.body.classList.add('no-hardware-acceleration')
  }

  // 如果不支持backdrop-filter，添加降级类
  if (!supportsBackdrop) {
    document.body.classList.add('no-backdrop-filter')
  }

  // 如果用户偏好减少动画，添加相应类
  if (reducedMotion) {
    document.body.classList.add('reduced-motion')
  }

  return {
    performanceLevel,
    supportsHardware,
    supportsBackdrop,
    reducedMotion
  }
}

// 懒加载优化
export const createIntersectionObserver = (callback, options = {}) => {
  const defaultOptions = {
    root: null,
    rootMargin: '50px',
    threshold: 0.1,
    ...options
  }

  return new IntersectionObserver(callback, defaultOptions)
}

// 防抖函数
export const debounce = (func, wait) => {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

// 节流函数
export const throttle = (func, limit) => {
  let inThrottle
  return function() {
    const args = arguments
    const context = this
    if (!inThrottle) {
      func.apply(context, args)
      inThrottle = true
      setTimeout(() => inThrottle = false, limit)
    }
  }
}

// 优化图片加载
export const optimizeImageLoading = () => {
  const images = document.querySelectorAll('img[data-src]')
  const imageObserver = createIntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target
        img.src = img.dataset.src
        img.classList.remove('lazy')
        imageObserver.unobserve(img)
      }
    })
  })

  images.forEach(img => imageObserver.observe(img))
}

// 优化动画性能
export const optimizeAnimations = () => {
  const animatedElements = document.querySelectorAll('[class*="animate-"]')

  animatedElements.forEach(element => {
    // 添加硬件加速
    element.style.transform = 'translateZ(0)'
    element.style.willChange = 'transform'
  })
}

// 减少重绘和重排
export const optimizeLayout = () => {
  // 批量DOM操作
  const batchDOMUpdates = (updates) => {
    requestAnimationFrame(() => {
      updates.forEach(update => update())
    })
  }

  return { batchDOMUpdates }
}

// 内存优化
export const optimizeMemory = () => {
  // 清理事件监听器
  const cleanupEventListeners = () => {
    // 在组件卸载时调用
  }

  // 清理定时器
  const cleanupTimers = () => {
    // 清理所有定时器
  }

  return { cleanupEventListeners, cleanupTimers }
}

// 初始化渲染性能优化
export const initRenderOptimizations = () => {
  // 应用基础优化
  const config = applyRenderOptimizations()

  // 根据性能等级应用不同策略
  switch (config.performanceLevel) {
    case 'low':
      // 低端设备：禁用复杂动画，简化UI
      document.body.classList.add('low-performance-mode')
      break
    case 'medium':
      // 中端设备：适度优化
      document.body.classList.add('medium-performance-mode')
      break
    case 'high':
      // 高端设备：启用所有功能
      document.body.classList.add('high-performance-mode')
      break
  }

  // 优化图片加载
  optimizeImageLoading()

  // 优化动画
  optimizeAnimations()

  return config
}
